import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import {
    MonitorCog , SquareActivity , Tickets , ArrowLeft, Settings, Users, Calendar, BarChart3, Bell, Search, RefreshCw, LogOut,
    Plus, MoreHorizontal, Filter, Download, Upload, Share2, Edit3, Trash2, Eye,
    CheckCircle, Clock, AlertCircle, User, Tag, Flag, Archive, Copy, ExternalLink,
    Building, Home, ChevronRight, ChevronDown, Menu, X, Sun, Moon, Target,
    TrendingUp, Activity, FileText, Folder, Code, Layers, MessageSquare,
    Paperclip, Calendar as CalendarIcon, Timer, Zap, Award, Shield, Globe,
    DollarSign, Briefcase, Star, Heart, Bookmark, GitBranch, Database , Save
} from 'lucide-react';
import '../Styles/kanban.css';

// Context for Project Data
const ProjectContext = createContext();
const useProject = () => useContext(ProjectContext);

// Theme Context
const ThemeContext = createContext();
const useTheme = () => useContext(ThemeContext);

// Notification Context
const NotificationContext = createContext();
const useNotifications = () => useContext(NotificationContext);

const ProjectDetails = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();

    // Core State
    const [user, setUser] = useState(null);
    const [project, setProject] = useState(null);
    const [columns, setColumns] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [members, setMembers] = useState([]);
    const [activities, setActivities] = useState([]);
    const [projectStats, setProjectStats] = useState(null);

    // UI State
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [theme, setTheme] = useState('light');
    const [notifications, setNotifications] = useState([]);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // View State
    const [activeView, setActiveView] = useState('overview'); // overview, board, tasks, members, analytics, activity
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPriority, setFilterPriority] = useState('all');
    const [filterAssignee, setFilterAssignee] = useState('all');

    // Modal States
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);

    // Initialize
    useEffect(() => {
        if (projectId) {
            initializeProjectPage();
        } else {
            navigate('/projects');
        }
    }, [projectId]);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const initializeProjectPage = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Check authentication
            const syncUser = ApiService.getUserSync();
            if (syncUser) {
                setUser(syncUser);
            } else {
                const userResponse = await ApiService.getCurrentUser();
                if (userResponse.success) {
                    setUser(userResponse.user);
                } else {
                    navigate('/login');
                    return;
                }
            }

            // Load all project data
            await Promise.all([
                loadProjectData(),
                loadProjectTasks(),
                loadProjectMembers(),
                loadProjectActivity(),
                loadProjectStats()
            ]);

        } catch (error) {
            console.error('Error initializing project page:', error);
            setError('Failed to load project data');
        } finally {
            setIsLoading(false);
        }
    };

    const loadProjectData = async () => {
        try {
            const response = await ApiService.getProject(projectId);
            if (response.success) {
                setProject(response.project);

                // Set columns from project data
                if (response.project.columns) {
                    setColumns(response.project.columns);
                } else {
                    // Load columns separately if not included
                    const columnsResponse = await ApiService.getProjectColumns(projectId);
                    if (columnsResponse.success) {
                        setColumns(columnsResponse.columns || []);
                    }
                }
            } else {
                throw new Error(response.message || 'Project not found');
            }
        } catch (error) {
            console.error('Error loading project:', error);
            throw error;
        }
    };

    const loadProjectTasks = async () => {
        try {
            const response = await ApiService.getProjectTasks(projectId);
            if (response.success) {
                setTasks(response.tasks || []);
            }
        } catch (error) {
            console.error('Error loading tasks:', error);
        }
    };

    const loadProjectMembers = async () => {
        try {
            const response = await ApiService.getProjectMembers(projectId);
            if (response.success) {
                setMembers(response.members || []);
            }
        } catch (error) {
            console.error('Error loading members:', error);
        }
    };

    const loadProjectActivity = async () => {
        try {
            const response = await ApiService.getProjectLogs(projectId, { limit: 20 });
            if (response.success) {
                setActivities(response.logs || []);
            } else {
                // Set empty array if no logs available
                setActivities([]);
            }
        } catch (error) {
            console.error('Error loading activity:', error);
            // Set empty array on error and don't throw
            setActivities([]);
        }
    };

    const loadProjectStats = async () => {
        try {
            const response = await ApiService.getProjectTaskStats(projectId);
            if (response.success) {
                setProjectStats(response.stats);
            }
        } catch (error) {
            console.error('Error loading project stats:', error);
        }
    };

    // Event Handlers
    const handleRefresh = () => {
        initializeProjectPage();
    };

    const handleLogout = async () => {
        if (window.confirm('Are you sure you want to logout?')) {
            ApiService.clearUserData();
            navigate('/login');
        }
    };

    const handleTaskCreate = async (taskData) => {
        try {
            const response = await ApiService.createTask(projectId, taskData);
            if (response.success) {
                setTasks(prev => [response.task, ...prev]);
                setShowTaskModal(false);
                addNotification({
                    type: 'success',
                    title: 'Task Created',
                    message: `Task "${taskData.title}" has been created successfully.`
                });
            }
        } catch (error) {
            addNotification({
                type: 'error',
                title: 'Error',
                message: 'Failed to create task'
            });
        }
    };

    const addNotification = (notification) => {
        const id = Date.now().toString();
        const newNotification = { ...notification, id };
        setNotifications(prev => [...prev, newNotification]);

        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 5000);
    };

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    // Utility Functions
    const getUserInitials = (userData = user) => {
        if (!userData) return 'U';
        if (userData.firstName && userData.lastName) {
            return (userData.firstName.charAt(0) + userData.lastName.charAt(0)).toUpperCase();
        }
        if (userData.name) {
            const nameParts = userData.name.split(' ');
            return nameParts.length >= 2
                ? (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase()
                : nameParts[0].charAt(0).toUpperCase();
        }
        return userData.email ? userData.email.charAt(0).toUpperCase() : 'U';
    };

    const getDisplayName = (userData = user) => {
        if (!userData) return 'User';
        if (userData.firstName && userData.lastName) {
            return `${userData.firstName} ${userData.lastName}`;
        }
        return userData.name || userData.email || 'User';
    };

    const getProjectProgress = () => {
        if (!projectStats) return 0;
        return projectStats.totalTasks > 0
            ? Math.round((projectStats.completedTasks / projectStats.totalTasks) * 100)
            : 0;
    };

    // Filter Functions
    const getFilteredTasks = () => {
        return tasks.filter(task => {
            const matchesSearch = !searchTerm ||
                task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                task.description?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = filterStatus === 'all' ||
                (filterStatus === 'completed' && task.completed) ||
                (filterStatus === 'pending' && !task.completed);

            const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;

            const matchesAssignee = filterAssignee === 'all' ||
                task.assignee?.userId === filterAssignee;

            return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
        });
    };

    // Context Values
    const projectContextValue = {
        project,
        columns,
        tasks,
        setTasks,
        members,
        activities,
        projectStats,
        getProjectProgress,
        loadProjectData,
        loadProjectTasks,
        loadProjectMembers,
        loadProjectActivity
    };

    const themeContextValue = { theme, setTheme, toggleTheme };

    const notificationContextValue = {
        notifications,
        addNotification,
        removeNotification: (id) => setNotifications(prev => prev.filter(n => n.id !== id))
    };

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (error) {
        return <ErrorScreen error={error} onRetry={handleRefresh} onBack={() => navigate('/projects')} />;
    }

    if (!project) {
        return <NotFoundScreen onBack={() => navigate('/projects')} />;
    }

    return (
        <ThemeContext.Provider value={themeContextValue}>
            <NotificationContext.Provider value={notificationContextValue}>
                <ProjectContext.Provider value={projectContextValue}>
                    <div className={`app project-details-app ${theme}`}>
                        <div className="app-container">
                            {/* Sidebar */}
                            <ProjectSidebar
                                collapsed={sidebarCollapsed}
                                onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                                activeView={activeView}
                                onViewChange={setActiveView}
                                user={user}
                                getUserInitials={getUserInitials}
                                getDisplayName={getDisplayName}
                                onLogout={handleLogout}
                                onBack={() => navigate('/projects')}
                            />

                            {/* Main Content */}
                            <div className="main-content">
                                {/* Header */}
                                <ProjectHeader
                                    currentView={activeView}
                                    searchTerm={searchTerm}
                                    onSearchChange={setSearchTerm}
                                    onRefresh={handleRefresh}
                                    onThemeToggle={toggleTheme}
                                    theme={theme}
                                    currentTime={currentTime}
                                    project={project}
                                    onBack={() => navigate('/projects')}
                                />

                                {/* Content Area */}
                                <div className="content-area">
                                    <ProjectContent
                                        activeView={activeView}
                                        searchTerm={searchTerm}
                                        filterStatus={filterStatus}
                                        filterPriority={filterPriority}
                                        filterAssignee={filterAssignee}
                                        onFilterStatusChange={setFilterStatus}
                                        onFilterPriorityChange={setFilterPriority}
                                        onFilterAssigneeChange={setFilterAssignee}
                                        onTaskCreate={() => setShowTaskModal(true)}
                                        onMemberInvite={() => setShowMemberModal(true)}
                                        onProjectSettings={() => setShowSettingsModal(true)}
                                        filteredTasks={getFilteredTasks()}
                                        user={user}
                                        getUserInitials={getUserInitials}
                                        setTasks={setTasks}
                                    />
                                </div>
                            </div>

                            {/* Notification Container */}
                            <NotificationContainer />

                            {/* Modals */}
                            {showTaskModal && (
                                <TaskModal
                                    isOpen={showTaskModal}
                                    onClose={() => setShowTaskModal(false)}
                                    onSubmit={handleTaskCreate}
                                    columns={columns}
                                    members={members}
                                    editingTask={editingTask}
                                />
                            )}

                            {showMemberModal && (
                                <MemberModal
                                    isOpen={showMemberModal}
                                    onClose={() => setShowMemberModal(false)}
                                    projectId={projectId}
                                    onMemberAdded={loadProjectMembers}
                                />
                            )}

                            {showSettingsModal && (
                                <ProjectSettingsModal
                                    isOpen={showSettingsModal}
                                    onClose={() => setShowSettingsModal(false)}
                                    project={project}
                                    onProjectUpdated={loadProjectData}
                                />
                            )}
                        </div>
                    </div>
                </ProjectContext.Provider>
            </NotificationContext.Provider>
        </ThemeContext.Provider>
    );
};

// Loading Screen Component
const LoadingScreen = () => (
    <div className="loading-screen">
        <div className="loading-content">
            <div className="loading-spinner"></div>
            <p className="loading-text">Loading Project...</p>
        </div>
    </div>
);

// Error Screen Component
const ErrorScreen = ({ error, onRetry, onBack }) => (
    <div className="error-screen">
        <div className="error-content">
            <AlertCircle style={{ width: '48px', height: '48px', color: '#ef4444', marginBottom: '16px' }} />
            <h2>Something went wrong</h2>
            <p>{error}</p>
            <div className="error-actions">
                <button className="btn btn-primary" onClick={onRetry}>
                    <RefreshCw style={{ width: '16px', height: '16px' }} />
                    Try Again
                </button>
                <button className="btn btn-secondary" onClick={onBack}>
                    <ArrowLeft style={{ width: '16px', height: '16px' }} />
                    Back to Projects
                </button>
            </div>
        </div>
    </div>
);

// Not Found Screen Component
const NotFoundScreen = ({ onBack }) => (
    <div className="error-screen">
        <div className="error-content">
            <Folder style={{ width: '48px', height: '48px', color: '#6b7280', marginBottom: '16px' }} />
            <h2>Project Not Found</h2>
            <p>The project you're looking for doesn't exist or you don't have access to it.</p>
            <button className="btn btn-primary" onClick={onBack}>
                <ArrowLeft style={{ width: '16px', height: '16px' }} />
                Back to Projects
            </button>
        </div>
    </div>
);

// Sidebar Component
const ProjectSidebar = ({
                            collapsed,
                            onToggle,
                            activeView,
                            onViewChange,
                            user,
                            getUserInitials,
                            getDisplayName,
                            onLogout,
                            onBack
                        }) => {
    const { theme } = useTheme();
    const { project } = useProject();

    const menuItems = [
        { id: 'overview', label: 'Overview', icon: Home },
        { id: 'board', label: 'Kanban Board', icon: Layers },
        { id: 'tasks', label: 'Task List', icon: CheckCircle },
        { id: 'members', label: 'Team Members', icon: Users },
        { id: 'analytics', label: 'Analytics', icon: BarChart3 },
        { id: 'activity', label: 'Activity Log', icon: Activity },
        { id: 'files', label: 'Files', icon: FileText },
        { id: 'settings', label: 'Settings', icon: Settings }
    ];

    return (
        <div className={`sidebar project-sidebar ${collapsed ? 'collapsed' : ''} ${theme}`}>
            <div className="sidebar-header">
                <div className="logo-section">
                    <button
                        className="back-to-projects"
                        onClick={onBack}
                        title="Back to Projects"
                    >
                        <ArrowLeft style={{ width: '16px', height: '16px' }} />
                    </button>
                    <button className="sidebar-toggle" onClick={onToggle}>
                        <Menu style={{ width: '16px', height: '16px' }} />
                    </button>
                </div>
            </div>

            <div className="sidebar-content">
                <nav className="sidebar-nav">
                    {menuItems.map(item => (
                        <button
                            key={item.id}
                            className={`nav-item ${activeView === item.id ? 'active' : ''}`}
                            onClick={() => onViewChange(item.id)}
                        >
                            <item.icon className="nav-icon" />
                            {!collapsed && <span className="nav-label">{item.label}</span>}
                        </button>
                    ))}
                </nav>

                {!collapsed && (
                    <div className="sidebar-footer">
                        <div className="user-profile">
                            <div className="user-avatar">
                                {getUserInitials()}
                            </div>
                            <div className="user-info">
                                <div className="user-name">{getDisplayName()}</div>
                                <div className="user-role">{user?.role || 'Member'}</div>
                            </div>
                            <button className="logout-button" onClick={onLogout} title="Logout">
                                <LogOut style={{ width: '16px', height: '16px' }} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Header Component
const ProjectHeader = ({
                           currentView,
                           searchTerm,
                           onSearchChange,
                           onRefresh,
                           onThemeToggle,
                           theme,
                           currentTime,
                           project,
                           onBack
                       }) => {
    const { notifications } = useNotifications();

    const getViewTitle = () => {
        const titles = {
            overview: 'Project Overview',
            board: 'Kanban Board',
            tasks: 'Task Management',
            members: 'Team Members',
            analytics: 'Project Analytics',
            activity: 'Activity Log',
            files: 'Project Files',
            settings: 'Project Settings'
        };
        return titles[currentView] || 'Project';
    };

    return (
        <header className="header project-header">
            <div className="header-content">
                <div className="header-left">
                    <h1 className="page-title">{getViewTitle()}</h1>
                    <div className="breadcrumb">
                        <span>UNITECH</span>
                        <ChevronRight style={{ width: '14px', height: '14px' }} />
                        <span>Projects</span>
                        <ChevronRight style={{ width: '14px', height: '14px' }} />
                        <span>{project?.name}</span>
                    </div>
                </div>

                <div className="header-center">
                    <div className="search-container">
                        <Search className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search tasks, members, files..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="search-input"
                        />
                    </div>
                </div>

                <div className="header-right">
                    <button className="header-action" onClick={onRefresh} title="Refresh">
                        <RefreshCw style={{ width: '18px', height: '18px' }} />
                    </button>

                    <button className="header-action" onClick={onThemeToggle} title="Toggle Theme">
                        {theme === 'light' ?
                            <Moon style={{ width: '18px', height: '18px' }} /> :
                            <Sun style={{ width: '18px', height: '18px' }} />
                        }
                    </button>

                    <button className="header-action" title="Notifications">
                        <Bell style={{ width: '18px', height: '18px' }} />
                        {notifications.length > 0 && (
                            <span className="notification-badge">{notifications.length}</span>
                        )}
                    </button>

                    <div className="time-display">
                        {currentTime.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </div>
                </div>
            </div>
        </header>
    );
};

// Content Component
const ProjectContent = ({
                            activeView,
                            searchTerm,
                            filterStatus,
                            filterPriority,
                            filterAssignee,
                            onFilterStatusChange,
                            onFilterPriorityChange,
                            onFilterAssigneeChange,
                            onTaskCreate,
                            onMemberInvite,
                            onProjectSettings,
                            filteredTasks,
                            user,
                            getUserInitials,
    setTasks

                        }) => {
    const renderContent = () => {
        switch (activeView) {
            case 'overview':
                return <ProjectOverview onTaskCreate={onTaskCreate} onMemberInvite={onMemberInvite} />;
            case 'board':
                return <KanbanBoard filteredTasks={filteredTasks} />;
            case 'tasks':
                return (
                    <TaskList
                        tasks={filteredTasks}
                        filterStatus={filterStatus}
                        filterPriority={filterPriority}
                        filterAssignee={filterAssignee}
                        onFilterStatusChange={onFilterStatusChange}
                        onFilterPriorityChange={onFilterPriorityChange}
                        onFilterAssigneeChange={onFilterAssigneeChange}
                        onTaskCreate={onTaskCreate}
                        setTasks={setTasks}
                    />
                );
            case 'members':
                return <TeamMembers onMemberInvite={onMemberInvite} getUserInitials={getUserInitials} />;
            case 'analytics':
                return <ProjectAnalytics />;
            case 'activity':
                return <ActivityLog />;
            case 'files':
                return <ProjectFiles />;
            case 'settings':
                return <ProjectSettings onProjectSettings={onProjectSettings} />;
            default:
                return <ProjectOverview onTaskCreate={onTaskCreate} onMemberInvite={onMemberInvite} />;
        }
    };

    return <div className="project-content">{renderContent()}</div>;
};

// Project Overview Component
const ProjectOverview = ({ onTaskCreate, onMemberInvite }) => {
    const { project, projectStats, members, tasks, activities } = useProject();

    const stats = [
        {
            label: 'Total Tasks',
            value: projectStats?.totalTasks || 0,
            change: '+12%',
            icon: CheckCircle,
            color: 'blue'
        },
        {
            label: 'Completed',
            value: projectStats?.completedTasks || 0,
            change: '+8%',
            icon: Target,
            color: 'green'
        },
        {
            label: 'Team Members',
            value: members.length,
            change: '+15%',
            icon: Users,
            color: 'purple'
        },
        {
            label: 'Completion Rate',
            value: `${projectStats?.completionRate || 0}%`,
            change: '+5%',
            icon: TrendingUp,
            color: 'orange'
        }
    ];

    const recentTasks = tasks.slice(0, 5);
    const recentActivity = activities.slice(0, 8);

    return (
        <div className="project-overview">
            {/* Project Header */}
            <div className="overview-header">
                <div className="project-title-section">
                    <h1>{project.name}</h1>
                    <p>{project.description}</p>
                    <div className="project-meta">
                        <span className="project-code">Code: {project.code}</span>
                        <span className="project-date">
                            Created: {new Date(project.createdAt).toLocaleDateString()}
                        </span>
                    </div>
                </div>
                <div className="overview-actions">
                    <button className="btn btn-primary" onClick={onTaskCreate}>
                        <Plus style={{ width: '16px', height: '16px' }} />
                        New Task
                    </button>
                    <button className="btn btn-secondary" onClick={onMemberInvite}>
                        <Users style={{ width: '16px', height: '16px' }} />
                        Invite Member
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                {stats.map((stat, index) => (
                    <div key={index} className={`stat-card ${stat.color}`}>
                        <div className="stat-header">
                            <div className="stat-icon">
                                <stat.icon />
                            </div>
                            <div className="stat-change">
                                <TrendingUp className="trend-icon" />
                                <span>{stat.change}</span>
                            </div>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stat.value}</div>
                            <div className="stat-label">{stat.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Content Grid */}
            <div className="overview-grid">
                {/* Recent Tasks */}
                <div className="overview-section">
                    <div className="section-header">
                        <h3>Recent Tasks</h3>
                        <button className="section-action">View All</button>
                    </div>
                    <div className="task-list">
                        {recentTasks.map(task => (
                            <TaskItem key={task._id} task={task} compact />
                        ))}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="overview-section">
                    <div className="section-header">
                        <h3>Recent Activity</h3>
                        <button className="section-action">View All</button>
                    </div>
                    <div className="activity-list">
                        {recentActivity.map(activity => (
                            <ActivityItem key={activity._id} activity={activity} />
                        ))}
                    </div>
                </div>

                {/* Team Members */}
                <div className="overview-section">
                    <div className="section-header">
                        <h3>Team Members</h3>
                        <button className="section-action">View All</button>
                    </div>
                    <div className="members-grid">
                        {members.slice(0, 6).map(member => (
                            <MemberCard key={member.userId} member={member} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Simplified placeholder components for other views
const KanbanBoard = ({ filteredTasks }) => {
    const { columns, tasks, setTasks, members, project } = useProject();
    const { addNotification } = useNotifications();
    const { projectId } = useParams();

    // Local state for drag and drop
    const [draggedTask, setDraggedTask] = useState(null);
    const [draggedOverColumn, setDraggedOverColumn] = useState(null);
    const [editingTask, setEditingTask] = useState(null);
    const [showColumnMenu, setShowColumnMenu] = useState(null);
    const [showTaskForm, setShowTaskForm] = useState(null);

    // Get tasks for a specific column
    const getColumnTasks = (columnId) => {
        return filteredTasks.filter(task => task.columnId === columnId);
    };

    // Drag and Drop handlers
    const handleDragStart = (e, task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e, columnId) => {
        e.preventDefault();
        setDraggedOverColumn(columnId);
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDraggedOverColumn(null);
        }
    };

    const handleDrop = async (e, targetColumnId) => {
        e.preventDefault();
        setDraggedOverColumn(null);

        if (!draggedTask || draggedTask.columnId === targetColumnId) {
            setDraggedTask(null);
            return;
        }

        try {
            // Update task in local state immediately
            setTasks(prevTasks =>
                prevTasks.map(task =>
                    task._id === draggedTask._id
                        ? { ...task, columnId: targetColumnId }
                        : task
                )
            );

            // API call to update task
            const response = await ApiService.updateTask(draggedTask._id, {
                columnId: targetColumnId
            });

            if (response.success) {
                addNotification({
                    type: 'success',
                    title: 'Task Moved',
                    message: `Task moved to ${columns.find(c => c._id === targetColumnId)?.title}`
                });
            } else {
                // Revert on failure
                setTasks(prevTasks =>
                    prevTasks.map(task =>
                        task._id === draggedTask._id
                            ? { ...task, columnId: draggedTask.columnId }
                            : task
                    )
                );
                addNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to move task'
                });
            }
        } catch (error) {
            console.error('Error moving task:', error);
            addNotification({
                type: 'error',
                title: 'Error',
                message: 'Failed to move task'
            });
        }

        setDraggedTask(null);
    };

    const handleTaskUpdate = async (taskId, updates) => {
        try {
            setTasks(prevTasks =>
                prevTasks.map(task =>
                    task._id === taskId ? { ...task, ...updates } : task
                )
            );

            const response = await ApiService.updateTask(taskId, updates);
            if (!response.success) {
                // Revert on failure
                setTasks(prevTasks =>
                    prevTasks.map(task =>
                        task._id === taskId ? { ...task } : task
                    )
                );
                addNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to update task'
                });
            }
        } catch (error) {
            console.error('Error updating task:', error);
        }
    };

    return (
        <div className="kanban-board">
            <div className="kanban-header">
                <h2>Kanban Board</h2>
                <div className="kanban-actions">
                    <span className="task-count">{filteredTasks.length} tasks</span>
                </div>
            </div>

            <div className="kanban-columns">
                {columns.map(column => (
                    <KanbanColumn
                        key={column._id}
                        column={column}
                        tasks={getColumnTasks(column._id)}
                        members={members}
                        projectId={projectId} // Pass projectId here
                        isDraggedOver={draggedOverColumn === column._id}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, column._id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column._id)}
                        onTaskDragStart={handleDragStart}
                        onTaskUpdate={handleTaskUpdate}
                        editingTask={editingTask}
                        setEditingTask={setEditingTask}
                        showTaskForm={showTaskForm}
                        setShowTaskForm={setShowTaskForm}
                    />
                ))}
            </div>
        </div>
    );
};

const KanbanColumn = ({
                          column,
                          tasks,
                          members,
                          projectId,
                          setTasks,
                          isDraggedOver,
                          onDragOver,
                          onDragEnter,
                          onDragLeave,
                          onDrop,
                          onTaskDragStart,
                          onTaskUpdate,
                          editingTask,
                          setEditingTask,
                          showTaskForm,
                          setShowTaskForm
                      }) => {
    const { addNotification } = useNotifications();
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [newTitle, setNewTitle] = useState(column.title);
    const [showColumnMenu, setShowColumnMenu] = useState(false);
    const getColumnColor = (title) => {
        const colors = {
            'to do': '#6366f1',
            'todo': '#6366f1',
            'backlog': '#6366f1',
            'in progress': '#f59e0b',
            'in-progress': '#f59e0b',
            'doing': '#f59e0b',
            'review': '#8b5cf6',
            'testing': '#8b5cf6',
            'done': '#10b981',
            'completed': '#10b981',
            'finished': '#10b981'
        };
        return colors[title.toLowerCase()] || '#6b7280';
    };

    const columnColor = getColumnColor(column.title);

    const handleTaskDelete = async (taskId) => {
        try {
            // Optimistic update
            setTasks(prevTasks => prevTasks.filter(task => task._id !== taskId));

            const response = await ApiService.deleteTask(taskId);
            if (response.success) {
                addNotification({
                    type: 'success',
                    title: 'Task Deleted',
                    message: 'Task has been deleted successfully'
                });
            } else {
                // Revert on failure
                loadProjectTasks(); // Reload tasks
                addNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to delete task'
                });
            }
        } catch (error) {
            console.error('Error deleting task:', error);
            addNotification({
                type: 'error',
                title: 'Error',
                message: 'Failed to delete task'
            });
        }
    };

    const handleTaskToggleComplete = async (taskId, completed) => {
        try {
            // Optimistic update
            setTasks(prevTasks =>
                prevTasks.map(task =>
                    task._id === taskId ? { ...task, completed } : task
                )
            );

            const response = await ApiService.updateTask(taskId, { completed });
            if (response.success) {
                addNotification({
                    type: 'success',
                    title: completed ? 'Task Completed' : 'Task Reopened',
                    message: completed ? 'Great job! 🎉' : 'Task reopened for work'
                });
            } else {
                // Revert on failure
                setTasks(prevTasks =>
                    prevTasks.map(task =>
                        task._id === taskId ? { ...task, completed: !completed } : task
                    )
                );
            }
        } catch (error) {
            console.error('Error updating task:', error);
        }
    };

    const handleTitleSave = async () => {
        if (newTitle.trim() && newTitle !== column.title) {
            try {
                // Update column title via API
                const response = await ApiService.updateColumn(column._id, { title: newTitle });
                if (response.success) {
                    // Update local state
                    setColumns(prev =>
                        prev.map(col =>
                            col._id === column._id ? { ...col, title: newTitle } : col
                        )
                    );
                    addNotification({
                        type: 'success',
                        title: 'Column Updated',
                        message: 'Column name updated successfully'
                    });
                }
            } catch (error) {
                console.error('Error updating column:', error);
                setNewTitle(column.title); // Revert
            }
        }
        setIsEditingTitle(false);
    };

    const handleTaskDuplicate = async (taskToDuplicate) => {
        try {
            const duplicatedTask = {
                ...taskToDuplicate,
                title: `${taskToDuplicate.title} (Copy)`,
                completed: false,
                createdAt: new Date().toISOString(),
                _id: `temp-${Date.now()}` // Temporary ID
            };

            // Add to local state immediately
            setTasks(prev => [duplicatedTask, ...prev]);

            const response = await ApiService.createTask(projectId, {
                title: duplicatedTask.title,
                description: duplicatedTask.description,
                priority: duplicatedTask.priority,
                dueDate: duplicatedTask.dueDate,
                assignee: duplicatedTask.assignee,
                columnId: duplicatedTask.columnId
            });

            if (response.success) {
                // Replace temporary task with real one
                setTasks(prev =>
                    prev.map(task =>
                        task._id === duplicatedTask._id ? response.task : task
                    )
                );
                addNotification({
                    type: 'success',
                    title: 'Task Duplicated',
                    message: 'Task has been duplicated successfully'
                });
            } else {
                // Remove temporary task on failure
                setTasks(prev => prev.filter(task => task._id !== duplicatedTask._id));
                addNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to duplicate task'
                });
            }
        } catch (error) {
            console.error('Error duplicating task:', error);
            addNotification({
                type: 'error',
                title: 'Error',
                message: 'Failed to duplicate task'
            });
        }
    };

    return (
        <div
            className={`kanban-column ${isDraggedOver ? 'drag-over' : ''}`}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            <div className="column-header" style={{ borderTopColor: columnColor }}>
                <div className="column-title">
                    <div
                        className="column-indicator"
                        style={{ backgroundColor: columnColor }}
                    ></div>

                    {isEditingTitle ? (
                        <div className="title-edit">
                            <input
                                type="text"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                onBlur={handleTitleSave}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleTitleSave();
                                    }
                                    if (e.key === 'Escape') {
                                        setNewTitle(column.title);
                                        setIsEditingTitle(false);
                                    }
                                }}
                                className="title-input"
                                autoFocus
                            />
                        </div>
                    ) : (
                        <h3
                            onClick={() => setIsEditingTitle(true)}
                            className="column-title-text"
                            title="Click to edit"
                        >
                            {column.title}
                        </h3>
                    )}

                    <span className="task-count">{tasks.length}</span>
                </div>
                <div className="column-actions">
                    <button
                        className="column-action"
                        onClick={() => setShowTaskForm(column._id)}
                        title="Add task"
                    >
                        <Plus style={{ width: '16px', height: '16px' }} />
                    </button>
                    <div className="column-menu">
                        <button
                            className="column-action"
                            onClick={() => setShowColumnMenu(!showColumnMenu)}
                            title="Column options"
                        >
                            <MoreHorizontal style={{ width: '16px', height: '16px' }} />
                        </button>
                        {showColumnMenu && (
                            <div className="column-dropdown">
                                <button
                                    className="dropdown-item"
                                    onClick={() => {
                                        setIsEditingTitle(true);
                                        setShowColumnMenu(false);
                                    }}
                                >
                                    <Edit3 style={{ width: '14px', height: '14px' }} />
                                    Rename Column
                                </button>
                                <button className="dropdown-item">
                                    <Archive style={{ width: '14px', height: '14px' }} />
                                    Archive Column
                                </button>
                                <div className="dropdown-divider"></div>
                                <button className="dropdown-item danger">
                                    <Trash2 style={{ width: '14px', height: '14px' }} />
                                    Delete Column
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="column-content">
                {showTaskForm === column._id && (
                    <QuickTaskForm
                        columnId={column._id}
                        projectId={projectId}
                        onCancel={() => setShowTaskForm(null)}
                        onSubmit={() => setShowTaskForm(null)}
                        members={members}
                    />
                )}

                <div className="tasks-list">
                    {tasks.map(task => (
                        <KanbanTask
                            key={task._id}
                            task={task}
                            members={members}
                            isEditing={editingTask === task._id}
                            onDragStart={(e) => onTaskDragStart(e, task)}
                            onEdit={() => setEditingTask(task._id)}
                            onSave={(updates) => {
                                onTaskUpdate(task._id, updates);
                                setEditingTask(null);
                            }}
                            onCancel={() => setEditingTask(null)}
                            onDelete={handleTaskDelete}
                            onToggleComplete={handleTaskToggleComplete}
                            onDuplicate={handleTaskDuplicate}
                        />
                    ))}
                </div>

                {tasks.length === 0 && (
                    <div className="empty-column">
                        <p>No tasks in this column</p>
                        <button
                            className="add-task-btn"
                            onClick={() => setShowTaskForm(column._id)}
                        >
                            <Plus style={{ width: '16px', height: '16px' }} />
                            Add a task
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Kanban Task Component
const KanbanTask = ({
                        task,
                        members,
                        isEditing,
                        onDragStart,
                        onEdit,
                        onSave,
                        onCancel,
                        onDelete, // Add this
                        onToggleComplete, // Add this
                        onDuplicate // Add this
                    }) => {

    const [showActions, setShowActions] = useState(false);


    // Add this useEffect to handle clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showActions && !event.target.closest('.task-menu')) {
                setShowActions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showActions]);

    const [editData, setEditData] = useState({
        title: task.title,
        description: task.description || '',
        priority: task.priority,
        dueDate: task.dueDate || '',
        assignee: task.assignee?.userId || ''
    });

    const getPriorityColor = (priority) => {
        const colors = {
            high: '#ef4444',
            medium: '#f59e0b',
            low: '#10b981'
        };
        return colors[priority] || '#6b7280';
    };

    const getStatusIcon = () => {
        if (task.completed) return CheckCircle;
        if (task.status === 'in-progress') return Clock;
        return AlertCircle;
    };

    const StatusIcon = getStatusIcon();
    const assignedMember = members.find(m => m.userId === task.assignee?.userId);

    const getUserInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase();
    };

    const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && !task.completed;

    if (isEditing) {
        return (
            <div className="kanban-task editing">
                <div className="task-edit-form">
                    <input
                        type="text"
                        value={editData.title}
                        onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                        className="edit-title"
                        placeholder="Task title..."
                    />
                    <textarea
                        value={editData.description}
                        onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                        className="edit-description"
                        placeholder="Description..."
                        rows="2"
                    />
                    <div className="edit-controls">
                        <select
                            value={editData.priority}
                            onChange={(e) => setEditData(prev => ({ ...prev, priority: e.target.value }))}
                            className="edit-priority"
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                        </select>
                        <input
                            type="date"
                            value={editData.dueDate}
                            onChange={(e) => setEditData(prev => ({ ...prev, dueDate: e.target.value }))}
                            className="edit-date"
                        />
                    </div>
                    <div className="edit-actions">
                        <button
                            className="btn-save"
                            onClick={() => onSave(editData)}
                        >
                            <Save style={{ width: '14px', height: '14px' }} />
                        </button>
                        <button
                            className="btn-cancel"
                            onClick={onCancel}
                        >
                            <X style={{ width: '14px', height: '14px' }} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`kanban-task ${task.completed ? 'completed' : ''} ${isOverdue ? 'overdue' : ''}`}
            draggable={!isEditing}
            onDragStart={onDragStart}
        >
            <div className="task-header">
                <div className="task-status">
                    <button
                        className="status-toggle"
                        onClick={() => onToggleComplete(task._id, !task.completed)}
                        title={task.completed ? 'Mark as incomplete' : 'Mark as complete'}
                    >
                        <StatusIcon
                            className={`status-icon ${task.completed ? 'completed' : task.status}`}
                            style={{ width: '16px', height: '16px' }}
                        />
                    </button>
                </div>
                <div className="task-actions">
                    <button className="task-action" onClick={onEdit} title="Edit task">
                        <Edit3 style={{ width: '14px', height: '14px' }} />
                    </button>
                    <div className="task-menu">
                        <button
                            className="task-action"
                            onClick={() => setShowActions(!showActions)}
                            title="More actions"
                        >
                            <MoreHorizontal style={{ width: '14px', height: '14px' }} />
                        </button>
                        {showActions && (
                            <div className="task-dropdown">
                                <button
                                    className="dropdown-item"
                                    onClick={() => {
                                        onDuplicate(task);
                                        setShowActions(false);
                                    }}
                                >
                                    <Copy style={{ width: '14px', height: '14px' }} />
                                    Duplicate
                                </button>
                                <button
                                    className="dropdown-item priority-toggle"
                                    onClick={() => {
                                        const priorities = ['low', 'medium', 'high'];
                                        const currentIndex = priorities.indexOf(task.priority);
                                        const nextPriority = priorities[(currentIndex + 1) % priorities.length];
                                        onSave({ priority: nextPriority });
                                        setShowActions(false);
                                    }}
                                >
                                    <Flag style={{ width: '14px', height: '14px' }} />
                                    Change Priority
                                </button>
                                <button
                                    className="dropdown-item archive"
                                    onClick={() => {
                                        onSave({ archived: !task.archived });
                                        setShowActions(false);
                                    }}
                                >
                                    <Archive style={{ width: '14px', height: '14px' }} />
                                    {task.archived ? 'Unarchive' : 'Archive'}
                                </button>
                                <div className="dropdown-divider"></div>
                                <button
                                    className="dropdown-item danger"
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to delete this task?')) {
                                            onDelete(task._id);
                                        }
                                        setShowActions(false);
                                    }}
                                >
                                    <Trash2 style={{ width: '14px', height: '14px' }} />
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="task-content">
                <h4 className="task-title">{task.title}</h4>
                {task.description && (
                    <p className="task-description">{task.description}</p>
                )}

                {/* Add some visual indicators */}
                <div className="task-indicators">
                    {task.archived && (
                        <span className="task-badge archived">
                            <Archive style={{ width: '12px', height: '12px' }} />
                            Archived
                        </span>
                    )}
                    {task.attachments && task.attachments.length > 0 && (
                        <span className="task-badge attachments">
                            <Paperclip style={{ width: '12px', height: '12px' }} />
                            {task.attachments.length}
                        </span>
                    )}
                </div>
            </div>

            <div className="task-footer">
                <div className="task-meta">
                    <div
                        className="priority-indicator"
                        style={{ backgroundColor: getPriorityColor(task.priority) }}
                        title={`${task.priority} priority`}
                    ></div>

                    {task.dueDate && (
                        <div className={`due-date ${isOverdue ? 'overdue' : ''}`}>
                            <CalendarIcon style={{ width: '12px', height: '12px' }} />
                            <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                        </div>
                    )}
                </div>

                {assignedMember && (
                    <div className="task-assignee" title={assignedMember.name}>
                        <div className="assignee-avatar">
                            {getUserInitials(assignedMember.name)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Quick Task Form Component
const QuickTaskForm = ({ columnId, projectId, onCancel, onSubmit, members }) => { // Add projectId prop
    const { addNotification } = useNotifications();
    const { setTasks } = useProject();
    const [taskData, setTaskData] = useState({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        assignee: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!taskData.title.trim()) return;

        try {
            const newTask = {
                ...taskData,
                columnId,
                completed: false,
                createdAt: new Date().toISOString(),
                _id: `temp-${Date.now()}` // Temporary ID
            };

            // Add to local state immediately
            setTasks(prev => [newTask, ...prev]);

            // API call to create task
            const response = await ApiService.createTask(projectId, {
                ...taskData,
                columnId
            });

            if (response.success) {
                // Replace temporary task with real one
                setTasks(prev =>
                    prev.map(task =>
                        task._id === newTask._id ? response.task : task
                    )
                );
                addNotification({
                    type: 'success',
                    title: 'Task Created',
                    message: 'Task created successfully'
                });
            } else {
                // Remove temporary task on failure
                setTasks(prev => prev.filter(task => task._id !== newTask._id));
                addNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to create task'
                });
            }

            onSubmit();
        } catch (error) {
            console.error('Error creating task:', error);
            addNotification({
                type: 'error',
                title: 'Error',
                message: 'Failed to create task'
            });
        }
    };

    return (
        <div className="quick-task-form">
            <form onSubmit={handleSubmit}>
                <input
                    type="text"
                    value={taskData.title}
                    onChange={(e) => setTaskData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter task title..."
                    className="task-title-input"
                    autoFocus
                />
                <textarea
                    value={taskData.description}
                    onChange={(e) => setTaskData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Add description..."
                    className="task-description-input"
                    rows="2"
                />
                <div className="form-row">
                    <select
                        value={taskData.priority}
                        onChange={(e) => setTaskData(prev => ({ ...prev, priority: e.target.value }))}
                        className="priority-select"
                    >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                    </select>
                    <input
                        type="date"
                        value={taskData.dueDate}
                        onChange={(e) => setTaskData(prev => ({ ...prev, dueDate: e.target.value }))}
                        className="date-input"
                    />
                    <select
                        value={taskData.assignee}
                        onChange={(e) => setTaskData(prev => ({ ...prev, assignee: e.target.value }))}
                        className="assignee-select"
                    >
                        <option value="">Unassigned</option>
                        {members.map(member => (
                            <option key={member.userId} value={member.userId}>
                                {member.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="form-actions">
                    <button type="submit" className="btn-primary" disabled={!taskData.title.trim()}>
                        Add Task
                    </button>
                    <button type="button" className="btn-secondary" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

const TaskList = ({
                      tasks,
                      filterStatus,
                      filterPriority,
                      filterAssignee,
                      onFilterStatusChange,
                      onFilterPriorityChange,
                      onFilterAssigneeChange,
                      onTaskCreate,
                      setTasks // Add this prop
                  }) => {
    const { members } = useProject();
    const [sortBy, setSortBy] = useState('created'); // created, priority, dueDate, title
    const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
    const [viewMode, setViewMode] = useState('list'); // list, grid

    // Sort tasks
    const sortedTasks = [...tasks].sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
            case 'title':
                comparison = a.title.localeCompare(b.title);
                break;
            case 'priority':
                const priorityOrder = { high: 3, medium: 2, low: 1 };
                comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
                break;
            case 'dueDate':
                const dateA = a.dueDate ? new Date(a.dueDate) : new Date('9999-12-31');
                const dateB = b.dueDate ? new Date(b.dueDate) : new Date('9999-12-31');
                comparison = dateA - dateB;
                break;
            case 'created':
            default:
                comparison = new Date(a.createdAt) - new Date(b.createdAt);
                break;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });

    return (
        <div className="task-list-view">
            <div className="view-header">
                <div className="header-left">
                    <h2>Task List</h2>
                    <span className="task-count">{tasks.length} tasks</span>
                </div>
                <div className="header-actions">
                    <button className="btn btn-primary" onClick={onTaskCreate}>
                        <Plus style={{ width: '16px', height: '16px' }} />
                        New Task
                    </button>
                </div>
            </div>

            {/* Filters and Controls */}
            <div className="task-controls">
                <div className="filters">
                    <select
                        value={filterStatus}
                        onChange={(e) => onFilterStatusChange(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                    </select>

                    <select
                        value={filterPriority}
                        onChange={(e) => onFilterPriorityChange(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Priority</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>

                    <select
                        value={filterAssignee}
                        onChange={(e) => onFilterAssigneeChange(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Assignees</option>
                        {members.map(member => (
                            <option key={member.userId} value={member.userId}>
                                {member.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="view-controls">
                    <div className="sort-controls">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="sort-select"
                        >
                            <option value="created">Created Date</option>
                            <option value="title">Title</option>
                            <option value="priority">Priority</option>
                            <option value="dueDate">Due Date</option>
                        </select>
                        <button
                            className="sort-order-btn"
                            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                        >
                            {sortOrder === 'asc' ?
                                <ChevronDown style={{ width: '16px', height: '16px' }} /> :
                                <ChevronRight style={{ width: '16px', height: '16px' }} />
                            }
                        </button>
                    </div>

                    <div className="view-mode-toggle">
                        <button
                            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                            title="List View"
                        >
                            <Menu style={{ width: '16px', height: '16px' }} />
                        </button>
                        <button
                            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => setViewMode('grid')}
                            title="Grid View"
                        >
                            <Layers style={{ width: '16px', height: '16px' }} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Tasks Container */}
            <div className={`tasks-container ${viewMode}`}>
                {sortedTasks.length > 0 ? (
                    sortedTasks.map(task => (
                        <TaskListItem
                            key={task._id}
                            task={task}
                            members={members}
                            setTasks={setTasks}
                            viewMode={viewMode}
                        />
                    ))
                ) : (
                    <div className="empty-state">
                        <CheckCircle style={{ width: '48px', height: '48px', color: '#6b7280' }} />
                        <h3>No tasks found</h3>
                        <p>Try adjusting your filters or create a new task</p>
                        <button className="btn btn-primary" onClick={onTaskCreate}>
                            <Plus style={{ width: '16px', height: '16px' }} />
                            Create First Task
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const TaskListItem = ({ task, members, setTasks, viewMode }) => {
    const { addNotification } = useNotifications();
    const { projectId } = useParams();
    const [isEditing, setIsEditing] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const [editData, setEditData] = useState({
        title: task.title,
        description: task.description || '',
        priority: task.priority,
        dueDate: task.dueDate || '',
        assignee: task.assignee?.userId || ''
    });

    const getPriorityColor = (priority) => {
        const colors = {
            high: '#ef4444',
            medium: '#f59e0b',
            low: '#10b981'
        };
        return colors[priority] || '#6b7280';
    };

    const getStatusIcon = () => {
        if (task.completed) return CheckCircle;
        if (task.status === 'in-progress') return Clock;
        return AlertCircle;
    };

    const StatusIcon = getStatusIcon();
    const assignedMember = members.find(m => m.userId === task.assignee?.userId);
    const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && !task.completed;

    const getUserInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase();
    };

    const handleToggleComplete = async (completed) => {
        try {
            setTasks(prevTasks =>
                prevTasks.map(t =>
                    t._id === task._id ? { ...t, completed } : t
                )
            );

            const response = await ApiService.updateTask(task._id, { completed });
            if (response.success) {
                addNotification({
                    type: 'success',
                    title: completed ? 'Task Completed' : 'Task Reopened',
                    message: completed ? 'Great job! 🎉' : 'Task reopened for work'
                });
            }
        } catch (error) {
            console.error('Error updating task:', error);
        }
    };

    const handleSave = async (updates) => {
        try {
            setTasks(prevTasks =>
                prevTasks.map(t =>
                    t._id === task._id ? { ...t, ...updates } : t
                )
            );

            const response = await ApiService.updateTask(task._id, updates);
            if (response.success) {
                addNotification({
                    type: 'success',
                    title: 'Task Updated',
                    message: 'Task has been updated successfully'
                });
            }
        } catch (error) {
            console.error('Error updating task:', error);
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            try {
                setTasks(prevTasks => prevTasks.filter(t => t._id !== task._id));

                const response = await ApiService.deleteTask(task._id);
                if (response.success) {
                    addNotification({
                        type: 'success',
                        title: 'Task Deleted',
                        message: 'Task has been deleted successfully'
                    });
                }
            } catch (error) {
                console.error('Error deleting task:', error);
            }
        }
    };

    const handleDuplicate = async () => {
        try {
            const duplicatedTask = {
                ...task,
                title: `${task.title} (Copy)`,
                completed: false,
                createdAt: new Date().toISOString(),
                _id: `temp-${Date.now()}`
            };

            setTasks(prev => [duplicatedTask, ...prev]);

            const response = await ApiService.createTask(projectId, {
                title: duplicatedTask.title,
                description: duplicatedTask.description,
                priority: duplicatedTask.priority,
                dueDate: duplicatedTask.dueDate,
                assignee: duplicatedTask.assignee,
                columnId: duplicatedTask.columnId
            });

            if (response.success) {
                setTasks(prev =>
                    prev.map(t =>
                        t._id === duplicatedTask._id ? response.task : t
                    )
                );
                addNotification({
                    type: 'success',
                    title: 'Task Duplicated',
                    message: 'Task has been duplicated successfully'
                });
            }
        } catch (error) {
            console.error('Error duplicating task:', error);
        }
    };

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showActions && !event.target.closest('.task-actions-menu')) {
                setShowActions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showActions]);

    if (isEditing) {
        return (
            <div className={`task-list-item editing ${viewMode}`}>
                <div className="task-edit-form">
                    <input
                        type="text"
                        value={editData.title}
                        onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                        className="edit-title"
                        placeholder="Task title..."
                    />
                    <textarea
                        value={editData.description}
                        onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                        className="edit-description"
                        placeholder="Description..."
                        rows="2"
                    />
                    <div className="edit-controls">
                        <select
                            value={editData.priority}
                            onChange={(e) => setEditData(prev => ({ ...prev, priority: e.target.value }))}
                            className="edit-select"
                        >
                            <option value="low">Low Priority</option>
                            <option value="medium">Medium Priority</option>
                            <option value="high">High Priority</option>
                        </select>
                        <input
                            type="date"
                            value={editData.dueDate}
                            onChange={(e) => setEditData(prev => ({ ...prev, dueDate: e.target.value }))}
                            className="edit-date"
                        />
                        <select
                            value={editData.assignee}
                            onChange={(e) => setEditData(prev => ({ ...prev, assignee: e.target.value }))}
                            className="edit-select"
                        >
                            <option value="">Unassigned</option>
                            {members.map(member => (
                                <option key={member.userId} value={member.userId}>
                                    {member.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="edit-actions">
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                handleSave(editData);
                                setIsEditing(false);
                            }}
                        >
                            <Save style={{ width: '14px', height: '14px' }} />
                            Save
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setIsEditing(false)}
                        >
                            <X style={{ width: '14px', height: '14px' }} />
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`task-list-item ${task.completed ? 'completed' : ''} ${isOverdue ? 'overdue' : ''} ${viewMode}`}>
            <div className="task-main">
                <div className="task-status">
                    <button
                        className="status-toggle"
                        onClick={() => handleToggleComplete(!task.completed)}
                        title={task.completed ? 'Mark as incomplete' : 'Mark as complete'}
                    >
                        <StatusIcon
                            className={`status-icon ${task.completed ? 'completed' : task.status}`}
                        />
                    </button>
                </div>

                <div className="task-content">
                    <div className="task-header">
                        <h4 className="task-title">{task.title}</h4>
                        <div className="task-meta-inline">
                            <div
                                className="priority-indicator"
                                style={{ backgroundColor: getPriorityColor(task.priority) }}
                                title={`${task.priority} priority`}
                            ></div>
                            <span className="priority-text">{task.priority}</span>
                        </div>
                    </div>

                    {task.description && (
                        <p className="task-description">{task.description}</p>
                    )}

                    <div className="task-meta">
                        {task.dueDate && (
                            <div className={`due-date ${isOverdue ? 'overdue' : ''}`}>
                                <CalendarIcon style={{ width: '14px', height: '14px' }} />
                                <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                            </div>
                        )}

                        {assignedMember && (
                            <div className="task-assignee">
                                <div className="assignee-avatar">
                                    {getUserInitials(assignedMember.name)}
                                </div>
                                <span className="assignee-name">{assignedMember.name}</span>
                            </div>
                        )}

                        <div className="task-created">
                            <Clock style={{ width: '14px', height: '14px' }} />
                            <span>{new Date(task.createdAt).toLocaleDateString()}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="task-actions-menu">
                <button
                    className="task-action-btn edit"
                    onClick={() => setIsEditing(true)}
                    title="Edit task"
                >
                    <Edit3 style={{ width: '16px', height: '16px' }} />
                </button>

                <div className="task-dropdown-menu">
                    <button
                        className="task-action-btn more"
                        onClick={() => setShowActions(!showActions)}
                        title="More actions"
                    >
                        <MoreHorizontal style={{ width: '16px', height: '16px' }} />
                    </button>

                    {showActions && (
                        <div className="task-dropdown">
                            <button
                                className="dropdown-item"
                                onClick={() => {
                                    handleDuplicate();
                                    setShowActions(false);
                                }}
                            >
                                <Copy style={{ width: '14px', height: '14px' }} />
                                Duplicate
                            </button>
                            <button
                                className="dropdown-item"
                                onClick={() => {
                                    const priorities = ['low', 'medium', 'high'];
                                    const currentIndex = priorities.indexOf(task.priority);
                                    const nextPriority = priorities[(currentIndex + 1) % priorities.length];
                                    handleSave({ priority: nextPriority });
                                    setShowActions(false);
                                }}
                            >
                                <Flag style={{ width: '14px', height: '14px' }} />
                                Change Priority
                            </button>
                            <button
                                className="dropdown-item"
                                onClick={() => {
                                    handleSave({ archived: !task.archived });
                                    setShowActions(false);
                                }}
                            >
                                <Archive style={{ width: '14px', height: '14px' }} />
                                {task.archived ? 'Unarchive' : 'Archive'}
                            </button>
                            <div className="dropdown-divider"></div>
                            <button
                                className="dropdown-item danger"
                                onClick={() => {
                                    handleDelete();
                                    setShowActions(false);
                                }}
                            >
                                <Trash2 style={{ width: '14px', height: '14px' }} />
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TeamMembers = ({ onMemberInvite, getUserInitials }) => {
    const { members } = useProject();

    return (
        <div className="team-members-view">
            <div className="view-header">
                <h2>Team Members</h2>
                <button className="btn btn-primary" onClick={onMemberInvite}>
                    <Plus style={{ width: '16px', height: '16px' }} />
                    Invite Member
                </button>
            </div>
            <div className="members-grid">
                {members.map(member => (
                    <div key={member.userId} className="member-card">
                        <div className="member-avatar">
                            {getUserInitials({ name: member.name })}
                        </div>
                        <div className="member-info">
                            <h4>{member.name}</h4>
                            <p>{member.role}</p>
                            <span className="member-email">{member.email}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ProjectAnalytics = () => {
    const { projectStats, tasks, activities } = useProject();

    const chartData = [
        { name: 'Mon', completed: 4, created: 6 },
        { name: 'Tue', completed: 3, created: 8 },
        { name: 'Wed', completed: 7, created: 5 },
        { name: 'Thu', completed: 5, created: 9 },
        { name: 'Fri', completed: 8, created: 4 },
        { name: 'Sat', completed: 2, created: 2 },
        { name: 'Sun', completed: 1, created: 3 }
    ];

    return (
        <div className="project-analytics">
            <div className="analytics-header">
                <h2>Project Analytics</h2>
                <div className="analytics-actions">
                    <button className="btn btn-secondary">
                        <Download style={{ width: '16px', height: '16px' }} />
                        Export Report
                    </button>
                </div>
            </div>

            <div className="analytics-grid">
                <div className="analytics-card">
                    <div className="card-header">
                        <h3>Task Completion Trend</h3>
                        <BarChart3 className="card-icon" />
                    </div>
                    <div className="chart-placeholder">
                        <div className="chart-bars">
                            {chartData.map((data, index) => (
                                <div key={index} className="chart-day">
                                    <div className="chart-bar completed" style={{ height: `${data.completed * 10}%` }}></div>
                                    <div className="chart-bar created" style={{ height: `${data.created * 8}%` }}></div>
                                    <span className="chart-label">{data.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="analytics-card">
                    <div className="card-header">
                        <h3>Task Priority Distribution</h3>
                        <Target className="card-icon" />
                    </div>
                    <div className="priority-stats">
                        <div className="priority-item high">
                            <div className="priority-color"></div>
                            <span>High Priority</span>
                            <span className="priority-count">{tasks.filter(t => t.priority === 'high').length}</span>
                        </div>
                        <div className="priority-item medium">
                            <div className="priority-color"></div>
                            <span>Medium Priority</span>
                            <span className="priority-count">{tasks.filter(t => t.priority === 'medium').length}</span>
                        </div>
                        <div className="priority-item low">
                            <div className="priority-color"></div>
                            <span>Low Priority</span>
                            <span className="priority-count">{tasks.filter(t => t.priority === 'low').length}</span>
                        </div>
                    </div>
                </div>

                <div className="analytics-card full-width">
                    <div className="card-header">
                        <h3>Performance Metrics</h3>
                        <Activity className="card-icon" />
                    </div>
                    <div className="metrics-grid">
                        <div className="metric-item">
                            <div className="metric-value">{projectStats?.completionRate || 0}%</div>
                            <div className="metric-label">Completion Rate</div>
                            <div className="metric-trend positive">+5%</div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value">{activities.length}</div>
                            <div className="metric-label">Total Activities</div>
                            <div className="metric-trend positive">+12%</div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value">{projectStats?.overdueTasks || 0}</div>
                            <div className="metric-label">Overdue Tasks</div>
                            <div className="metric-trend negative">-3%</div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value">4.8</div>
                            <div className="metric-label">Team Velocity</div>
                            <div className="metric-trend positive">+0.3</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ActivityLog = () => {
    const { activities, loadProjectActivity } = useProject();

    return (
        <div className="activity-log">
            <div className="activity-header">
                <h2>Activity Log</h2>
                <button className="btn btn-secondary" onClick={loadProjectActivity}>
                    <RefreshCw style={{ width: '16px', height: '16px' }} />
                    Refresh
                </button>
            </div>

            <div className="activity-timeline">
                {activities.map(activity => (
                    <ActivityItem key={activity._id} activity={activity} detailed />
                ))}
            </div>
        </div>
    );
};

const ProjectFiles = () => {
    const mockFiles = [
        { id: 1, name: 'Project Requirements.pdf', size: '2.4 MB', uploadedBy: 'John Doe', uploadedAt: '2024-01-15' },
        { id: 2, name: 'Design Mockups.zip', size: '15.8 MB', uploadedBy: 'Jane Smith', uploadedAt: '2024-01-14' },
        { id: 3, name: 'Technical Specs.docx', size: '1.2 MB', uploadedBy: 'Mike Johnson', uploadedAt: '2024-01-13' }
    ];

    return (
        <div className="project-files">
            <div className="files-header">
                <h2>Project Files</h2>
                <div className="files-actions">
                    <button className="btn btn-secondary">
                        <Upload style={{ width: '16px', height: '16px' }} />
                        Upload File
                    </button>
                    <button className="btn btn-secondary">
                        <Folder style={{ width: '16px', height: '16px' }} />
                        New Folder
                    </button>
                </div>
            </div>

            <div className="files-grid">
                {mockFiles.map(file => (
                    <div key={file.id} className="file-card">
                        <div className="file-icon">
                            <FileText style={{ width: '24px', height: '24px' }} />
                        </div>
                        <div className="file-info">
                            <h4>{file.name}</h4>
                            <p>{file.size} • {file.uploadedBy}</p>
                            <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                        </div>
                        <div className="file-actions">
                            <button className="file-action">
                                <Download style={{ width: '16px', height: '16px' }} />
                            </button>
                            <button className="file-action">
                                <Share2 style={{ width: '16px', height: '16px' }} />
                            </button>
                            <button className="file-action">
                                <MoreHorizontal style={{ width: '16px', height: '16px' }} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ProjectSettings = ({ onProjectSettings }) => {
    const { project } = useProject();

    return (
        <div className="project-settings">
            <div className="settings-header">
                <h2>Project Settings</h2>
            </div>

            <div className="settings-sections">
                <div className="settings-section">
                    <h3>General Information</h3>
                    <div className="settings-form">
                        <div className="form-group">
                            <label>Project Name</label>
                            <input type="text" defaultValue={project?.name} />
                        </div>
                        <div className="form-group">
                            <label>Description</label>
                            <textarea defaultValue={project?.description}></textarea>
                        </div>
                        <div className="form-group">
                            <label>Project Code</label>
                            <input type="text" defaultValue={project?.code} disabled />
                        </div>
                    </div>
                </div>

                <div className="settings-section">
                    <h3>Access & Permissions</h3>
                    <div className="permission-items">
                        <div className="permission-item">
                            <div className="permission-info">
                                <h4>Allow Member Invitations</h4>
                                <p>Members can invite other users to this project</p>
                            </div>
                            <div className="permission-control">
                                <input type="checkbox" defaultChecked />
                            </div>
                        </div>
                        <div className="permission-item">
                            <div className="permission-info">
                                <h4>Public Project</h4>
                                <p>Anyone in your organization can view this project</p>
                            </div>
                            <div className="permission-control">
                                <input type="checkbox" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="settings-section danger-zone">
                    <h3>Danger Zone</h3>
                    <div className="danger-actions">
                        <button className="btn btn-danger">
                            <Archive style={{ width: '16px', height: '16px' }} />
                            Archive Project
                        </button>
                        <button className="btn btn-danger">
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                            Delete Project
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper Components
const TaskItem = ({ task, compact = false }) => {
    const getPriorityColor = (priority) => {
        const colors = { high: 'red', medium: 'orange', low: 'green' };
        return colors[priority] || 'gray';
    };

    const getStatusIcon = (status) => {
        if (task.completed) return CheckCircle;
        return status === 'in-progress' ? Clock : AlertCircle;
    };

    const StatusIcon = getStatusIcon(task.status);

    return (
        <div className={`task-item ${compact ? 'compact' : ''}`}>
            <div className="task-status">
                <StatusIcon className={`status-icon ${task.completed ? 'completed' : task.status}`} />
            </div>
            <div className="task-content">
                <div className="task-header">
                    <h5 className="task-title">{task.title}</h5>
                    <span className={`priority-badge ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                    </span>
                </div>
                {!compact && task.description && (
                    <p className="task-description">{task.description}</p>
                )}
                <div className="task-meta">
                    {task.assignee && (
                        <span className="task-assignee">
                            <User className="meta-icon" />
                            {task.assignee.name || 'Unassigned'}
                        </span>
                    )}
                    {task.dueDate && (
                        <span className="task-due">
                            <CalendarIcon className="meta-icon" />
                            {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                    )}
                </div>
            </div>
            <div className="task-actions">
                <button className="task-action">
                    <Edit3 style={{ width: '14px', height: '14px' }} />
                </button>
                <button className="task-action">
                    <MoreHorizontal style={{ width: '14px', height: '14px' }} />
                </button>
            </div>
        </div>
    );
};

const ActivityItem = ({ activity, detailed = false }) => {
    const getActivityIcon = (action) => {
        if (action.includes('task')) return CheckCircle;
        if (action.includes('member')) return Users;
        if (action.includes('project')) return Folder;
        return Activity;
    };

    const ActivityIcon = getActivityIcon(activity.action);

    return (
        <div className={`activity-item ${detailed ? 'detailed' : ''}`}>
            <div className="activity-icon">
                <ActivityIcon style={{ width: '16px', height: '16px' }} />
            </div>
            <div className="activity-content">
                <div className="activity-description">
                    <strong>{activity.user?.firstName} {activity.user?.lastName}</strong> {activity.description}
                </div>
                <div className="activity-time">
                    {new Date(activity.timestamp).toLocaleString()}
                </div>
            </div>
        </div>
    );
};

const MemberCard = ({ member }) => {
    const getUserInitials = (name) => {
        return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase();
    };

    return (
        <div className="member-card compact">
            <div className="member-avatar">
                {getUserInitials(member.name)}
            </div>
            <div className="member-info">
                <h5>{member.name}</h5>
                <span className="member-role">{member.role}</span>
            </div>
        </div>
    );
};

// Modal Components
const TaskModal = ({ isOpen, onClose, onSubmit, columns, members, editingTask }) => {
    const [taskData, setTaskData] = useState({
        title: editingTask?.title || '',
        description: editingTask?.description || '',
        columnId: editingTask?.columnId || columns[0]?._id || '',
        priority: editingTask?.priority || 'medium',
        dueDate: editingTask?.dueDate || '',
        assignee: editingTask?.assignee?.userId || '',
        tags: editingTask?.tags || []
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(taskData);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content task-modal">
                <div className="modal-header">
                    <h2>{editingTask ? 'Edit Task' : 'Create New Task'}</h2>
                    <button className="modal-close" onClick={onClose}>
                        <X style={{ width: '20px', height: '20px' }} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label>Task Title *</label>
                        <input
                            type="text"
                            value={taskData.title}
                            onChange={(e) => setTaskData(prev => ({ ...prev, title: e.target.value }))}
                            required
                            placeholder="Enter task title..."
                        />
                    </div>

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            value={taskData.description}
                            onChange={(e) => setTaskData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Enter task description..."
                            rows="3"
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Column</label>
                            <select
                                value={taskData.columnId}
                                onChange={(e) => setTaskData(prev => ({ ...prev, columnId: e.target.value }))}
                            >
                                {columns.map(column => (
                                    <option key={column._id} value={column._id}>
                                        {column.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Priority</label>
                            <select
                                value={taskData.priority}
                                onChange={(e) => setTaskData(prev => ({ ...prev, priority: e.target.value }))}
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Due Date</label>
                            <input
                                type="date"
                                value={taskData.dueDate}
                                onChange={(e) => setTaskData(prev => ({ ...prev, dueDate: e.target.value }))}
                            />
                        </div>

                        <div className="form-group">
                            <label>Assignee</label>
                            <select
                                value={taskData.assignee}
                                onChange={(e) => setTaskData(prev => ({ ...prev, assignee: e.target.value }))}
                            >
                                <option value="">Unassigned</option>
                                {members.map(member => (
                                    <option key={member.userId} value={member.userId}>
                                        {member.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary">
                            {editingTask ? 'Update Task' : 'Create Task'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const MemberModal = ({ isOpen, onClose, projectId, onMemberAdded }) => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            // API call to invite member
            // const response = await ApiService.inviteProjectMember(projectId, email);
            // if (response.success) {
            onMemberAdded();
            onClose();
            setEmail('');
            // }
        } catch (error) {
            console.error('Error inviting member:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content member-modal">
                <div className="modal-header">
                    <h2>Invite Team Member</h2>
                    <button className="modal-close" onClick={onClose}>
                        <X style={{ width: '20px', height: '20px' }} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label>Email Address *</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="Enter team member's email..."
                        />
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isLoading}>
                            {isLoading ? 'Sending...' : 'Send Invitation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ProjectSettingsModal = ({ isOpen, onClose, project, onProjectUpdated }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content settings-modal">
                <div className="modal-header">
                    <h2>Project Settings</h2>
                    <button className="modal-close" onClick={onClose}>
                        <X style={{ width: '20px', height: '20px' }} />
                    </button>
                </div>

                <div className="modal-body">
                    <p>Project settings functionality will be implemented here.</p>
                </div>

                <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// Notification Container
const NotificationContainer = () => {
    const { notifications, removeNotification } = useNotifications();

    return (
        <div className="notification-container">
            {notifications.map(notification => (
                <div key={notification.id} className={`notification ${notification.type}`}>
                    <div className="notification-content">
                        <div className="notification-title">{notification.title}</div>
                        <div className="notification-message">{notification.message}</div>
                    </div>
                    <button
                        className="notification-close"
                        onClick={() => removeNotification(notification.id)}
                    >
                        <X style={{ width: '16px', height: '16px' }} />
                    </button>
                </div>
            ))}
        </div>
    );
};

export default ProjectDetails;

//